// WebSocketProvider.js

import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from 'react';
import WebSocketManager from './WebSocketManager';

const WebSocketContext = createContext(null);

export const WebSocketProvider = ({ children }) => {
  const manager = useMemo(() => new WebSocketManager(), []);
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    totalFiles: 0,
    processedFiles: 0,
    successful: 0,
    duplicates: 0,
    failed: 0,
    overallProgress: 0,
    fileStatuses: {}, // Detailed status for each file
  });
  const uniqueIdRef = useRef(`${Math.random()}-${Date.now()}`); // useRef a stabil azonosítóhoz

  const wsProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';

  let url;
  if (window.location.hostname === 'localhost') {
    // 💻 fejlesztés alatt közvetlenül a Node szerverhez csatlakozik
    url = `${wsProtocol}://localhost:8081`;
  } else {
    // 🌐 szerveren Nginx proxy mögött
    url = `${wsProtocol}://${window.location.host}/ws/`;
  }
  const decodeURIComponentSafe = (str) => {
    try {
      return decodeURIComponent(str);
    } catch {
      return str;
    }
  };

  useEffect(() => {
    const handleWebSocketMessage = (msg) => {
      const data = JSON.parse(msg.data);
      setMessages((prev) => [...prev, data]); // Keep general messages

      if (data.type === 'upload_start') {
        setUploadProgress({
          totalFiles: data.totalFiles,
          processedFiles: 0,
          successful: 0,
          duplicates: 0,
          failed: 0,
          overallProgress: 0,
          fileStatuses: {},
        });
      } else if (data.type === 'upload_progress' && data.filename) {
        const FINAL_STATUSES = ['success', 'duplicate', 'error', 'failed'];

        setUploadProgress(prev => {
          const cleanFileName = decodeURIComponentSafe(data.filename);

          const prevStatus = prev.fileStatuses[cleanFileName]?.status;
          const isFinal = FINAL_STATUSES.includes(data.status);

          const newFileStatuses = {
            ...prev.fileStatuses,
            [cleanFileName]: {
              status: data.status,
              message: data.message,
              progress: data.progress || 100,
            },
          };

          let newSuccessful = prev.successful;
          let newDuplicates = prev.duplicates;
          let newFailed = prev.failed;
          let newProcessedFiles = prev.processedFiles;

          // 🔹 Csak akkor növeljük, ha most ér véget először (tehát korábban nem volt végleges státusza)
          if (isFinal && !FINAL_STATUSES.includes(prevStatus)) {
            newProcessedFiles++;

            if (data.status === 'success') newSuccessful++;
            else if (data.status === 'duplicate') newDuplicates++;
            else if (data.status === 'error' || data.status === 'failed') newFailed++;
          }

          const newOverallProgress =
            prev.totalFiles > 0
              ? ((newSuccessful + newDuplicates + newFailed) / prev.totalFiles) * 100
              : 0;

          return {
            ...prev,
            processedFiles: newProcessedFiles,
            successful: newSuccessful,
            duplicates: newDuplicates,
            failed: newFailed,
            overallProgress: newOverallProgress,
            fileStatuses: newFileStatuses,
          };
        });

      } else if (data.type === 'upload_complete') {
        // Optionally reset or finalize progress when all uploads are done
        setUploadProgress(prev => ({
          ...prev,
          overallProgress: 100, // Ensure it's 100% when complete
          // You might want to keep the summary for a short period
        }));
      }
    };

    const config = {
      reconnect: true,
      onOpen: () => setIsConnected(true),
      onClose: () => setIsConnected(false),
      onMessage: handleWebSocketMessage,
    };
    const currentId = uniqueIdRef.current;
    // Kapcsolat létrehozása
    manager.connect(uniqueIdRef.current, url, config);

    // A kapcsolat bontása csak a komponens teljes eltávolításakor történjen
    return () => {
      manager.disconnect(currentId);
    };
  }, [manager, url]);

  return (
    <WebSocketContext.Provider value={{ manager, messages, isConnected, uploadProgress }}>
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
};
